import { useCallback, useReducer } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as userActions from "../../../../redux/actions/user";
import { orderMethods, orderTypeIds } from "../../../../shared/constants/order";
import { shippingMethods } from "../../../../shared/constants/store";
import * as orderRepo from "../../../../shared/repos/graphql/order";
import * as productRepo from "../../../../shared/repos/graphql/product";
import { saveDeliveryAddress } from "../utils/addressStorage";
import ddLog from "../../../../shared/utilities/datadog/log";
import * as elementsActions from "../../../../redux/actions/elements";
import { runWithLocationAccessAcknowledgement } from "../../../../shared/utilities/locationAccessAcknowledgement";

/**
 * @typedef {Object} Store
 * @property {string} id
 * @property {string} name
 * @property {boolean} hasLocationAccessRestriction
 * @property {string} locationAccessMessage
 *
 * @typedef {Object} MutationParams
 * @property {() => void} [onSuccess]
 * @property {(error: Error) => void} [onError]
 */

/**
 * @typedef {Object} PickupConfirmation
 * @property {boolean} pending
 * @property {Error | null} error
 * @property {(currentStore: Store, params: MutationParams) => Promise<void>} mutate
 *
 * @returns {PickupConfirmation}
 */
export default function usePickupConfirmation() {
  const dispatch = useDispatch();
  const setModalObject = useCallback(
    value => dispatch(elementsActions.setModalObject(value)),
    [dispatch]
  );
  const userCartId = useSelector(state => state.user.userCartId);
  const userAddress = useSelector(state => state.user.userAddress);
  const userOrderMethod = useSelector(state => state.user.userOrderMethod);
  const [state, setState] = useReducer(
    (currentState, partialState) => ({
      ...currentState,
      ...partialState
    }),
    {
      error: null,
      pending: false
    }
  );

  const checkStockStatus = useCallback(
    async storeId => {
      const stockResponse = await productRepo.checkStoreProductStock(
        userCartId,
        storeId
      );

      const oosProducts = stockResponse.data.storeProductStock.filter(
        item => parseInt(item.oos, 10) === 1
      );

      if (oosProducts.length) {
        throw new Error("OUT_OF_STOCK_PRODUCTS", {
          cause: {
            products: oosProducts,
            orderProductIds: oosProducts.map(p => p.orderProductId)
          }
        });
      }
    },
    [userCartId]
  );

  const handleConfirmPickup = useCallback(
    /**
     * @param {Store} currentStore
     * @param {MutationParams} [params]
     * @returns {Promise<void>}
     */
    async (currentStore, params = {}) => {
      if (currentStore === undefined || !userCartId) return;

      setState({ pending: true });

      const proceed = async () => {
        try {
          if (userOrderMethod === orderMethods.delivery && userAddress) {
            saveDeliveryAddress(userCartId, userAddress);
          }

          const address = {
            address1: currentStore.mailingAddress,
            city: currentStore.city,
            state: currentStore.state,
            postcode: currentStore.zip
          };

          const data = {
            storeId: currentStore.id,
            orderTypeId: orderTypeIds.pickup,
            shippingMethod: shippingMethods.pickup,
            address
          };

          await checkStockStatus(currentStore.id);
          await orderRepo.updateOrderLocation(userCartId, data);
          await orderRepo.getOrder(userCartId);
          await orderRepo.setTip(userCartId, 0);
          await orderRepo.updateOrderComment(userCartId, "");

          dispatch(userActions.setUserOrderMethod(orderMethods.pickup));
          dispatch(userActions.setSelectedStore(currentStore));
          dispatch(userActions.setUserAddress(address));

          if (params.onSuccess) {
            params.onSuccess();
          }

          return true;
        } catch (error) {
          ddLog.logError(error);
          setState({ error });

          if (params.onError) {
            params.onError(error);
          }
          return false;
        }
      };

      try {
        await runWithLocationAccessAcknowledgement(
          currentStore,
          setModalObject,
          proceed
        );
      } finally {
        setState({ pending: false });
      }
    },
    [dispatch, setModalObject, userCartId, checkStockStatus, userOrderMethod, userAddress]
  );

  return {
    ...state,
    mutate: handleConfirmPickup
  };
}
